import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBinancePairsTicker } from '../../../../hooks/useBinancePairsTicker';
import { usePersistentPairSelection } from '../../../../hooks/usePersistentPairSelection';
import { UpDownChartBoard } from './components';
import { EZ5050TradingLayout } from '../../../../components/trading/EZ5050TradingLayout';
import { useWallet } from '../../../../contexts/walletContext';
import Axios from '../../../../Axios';

const UP_DOWN_TIMEFRAME_STORAGE_KEY = 'futures.up-down.timeframe';
const UP_DOWN_CHART_TYPE_STORAGE_KEY = 'futures.up-down.chartType';
const UP_DOWN_TIMEFRAME_OPTIONS = new Set(['Tick', '5s', '1m', '5m', '15m', '30m', '1h']);
const UP_DOWN_CHART_TYPE_OPTIONS = new Set(['candle', 'area']);

export function UpDownModeScreen() {
  const [rawPairs, setRawPairs] = useState([]);
  const [timeframe, setTimeframe] = useState(() => readStoredUpDownTimeframe());
  const [chartType, setChartType] = useState(() => readStoredUpDownChartType());
  const { wallet } = useWallet();
  const [luckyData, setLuckyData] = useState(null);

  const getLuckyRoundDetails = useCallback(async () => {
    try {
      const { data } = await Axios.get('/users/getLuckyRoundDetails');
      if (data?.success && data?.result?.isLucky && data?.result?.multiplier > 1) {
        setLuckyData(data.result);
      } else {
        setLuckyData(null);
      }
    } catch (error) {
      console.error('Error fetching lucky round details:', error);
    }
  }, []);

  useEffect(() => {
    getLuckyRoundDetails();

    const handleRefresh = () => {
      getLuckyRoundDetails();
    };
    const handleClearLucky = () => {
      setLuckyData(null);
    };

    window.addEventListener("refresh-lucky-details", handleRefresh);
    window.addEventListener("clear-lucky-multiplier", handleClearLucky);
    return () => {
      window.removeEventListener("refresh-lucky-details", handleRefresh);
      window.removeEventListener("clear-lucky-multiplier", handleClearLucky);
    };
  }, [getLuckyRoundDetails]);

  useEffect(() => {
    let cancelled = false;
    const fetchPairs = async () => {
      try {
        const { data } = await Axios.get('/binaryOptions/pairs');
        if (!cancelled && data?.success && Array.isArray(data.result)) {
          setRawPairs(data.result);
        }
      } catch (error) {
        console.error('Error fetching up-down pairs:', error);
      }
    };
    fetchPairs();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(UP_DOWN_TIMEFRAME_STORAGE_KEY, timeframe);
    } catch (_error) {
      // Ignore storage failures in restricted contexts.
    }
  }, [timeframe]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(UP_DOWN_CHART_TYPE_STORAGE_KEY, chartType);
    } catch (_error) {
      // Ignore storage failures in restricted contexts.
    }
  }, [chartType]);

  const topPairs = useMemo(() => rawPairs.map(normalizePair), [rawPairs]);

  const [selectedPair, setSelectedPair] = usePersistentPairSelection(
    'futures.up-down.selectedPair',
    topPairs,
    'BTCUSDT'
  );

  const pairStats = useBinancePairsTicker(topPairs, 'crypto');
  const activePairConfig = topPairs.find((pair) => pair.symbol === selectedPair) ?? topPairs[0];
  const activePair = pairStats.find((pair) => pair.symbol === selectedPair) ?? pairStats[0];

  const activePairLabel = useMemo(
    () => activePair?.label ?? activePairConfig?.label ?? 'BTC/USDT',
    [activePair?.label, activePairConfig?.label]
  );

  const activePairStartPrice = (Number.isFinite(activePair?.price) ? activePair.price : null)
    ?? activePairConfig?.fallbackPrice
    ?? 65948.64;

  return (
    <EZ5050TradingLayout
      wallet={wallet}
      activePairLabel={activePairLabel}
      payoutRate={activePairConfig?.payoutMultiplier ? activePairConfig.payoutMultiplier - 1 : 0.82}
      luckyData={luckyData}
    >
      <UpDownChartBoard
        symbol={activePairLabel}
        marketSymbol={selectedPair}
        startingPrice={activePairStartPrice}
        timeframe={timeframe}
        onTimeframeChange={setTimeframe}
        chartType={chartType}
        onChartTypeChange={setChartType}
        pairs={pairStats}
        selectedPair={selectedPair}
        onSelectPair={setSelectedPair}
        shouldShowLuckyModal={true}
        luckyData={luckyData}
        getLuckyRoundDetails={getLuckyRoundDetails}
      />
    </EZ5050TradingLayout>
  );
}

function normalizePair(entry) {
  const symbol = String(entry.pair || '').replaceAll('_', '').toUpperCase();
  const rawLabel = String(entry.displayName || entry.pair || symbol);
  const label = rawLabel.includes('/') ? rawLabel.toUpperCase() : rawLabel.replace('_', '/').toUpperCase();
  return {
    symbol,
    label,
    icon: entry.icon || '',
    fallbackPrice: Number.isFinite(Number(entry.price)) && Number(entry.price) > 0
      ? Number(entry.price)
      : NaN,
    minStake: Number(entry.minStake) || 1,
    maxStake: Number(entry.maxStake) || 100,
    payoutMultiplier: Number(entry.payoutMultiplier) || 1.85,
  };
}

function readStoredUpDownTimeframe() {
  if (typeof window === 'undefined') {
    return 'Tick';
  }

  try {
    const value = window.localStorage.getItem(UP_DOWN_TIMEFRAME_STORAGE_KEY);
    return UP_DOWN_TIMEFRAME_OPTIONS?.has(value) ? value : 'Tick';
  } catch (_error) {
    return 'Tick';
  }
}

function readStoredUpDownChartType() {
  if (typeof window === 'undefined') {
    return 'candle';
  }

  try {
    const value = window.localStorage.getItem(UP_DOWN_CHART_TYPE_STORAGE_KEY);
    return UP_DOWN_CHART_TYPE_OPTIONS?.has(value) ? value : 'candle';
  } catch (_error) {
    return 'candle';
  }
}
